import { createContext, useContext } from "react";

// Per-mount widget mode — isEmbedded/isCompact/isBar all depend on *which*
// widget instance is asking, not a single page-wide fact. This used to be a
// shared mutable singleton object, but a page can legitimately have more
// than one widget instance mounted at once (eg. the bar + modal handoff
// pattern in widget-helpers.js: a bar-mode mount and a full-size modal
// mount coexisting). A singleton meant the modal mounting would silently
// overwrite the bar's own isBar/isCompact flags — so any state update in
// the *bar* after the modal had mounted (eg. switching its plate/VIN tab)
// re-rendered it against the modal's mode instead of its own, expanding it
// into the full marketing page inside its ~100px strip. Context scopes the
// value to each <App> tree instead.
const WidgetModeContext = createContext({ isEmbedded: false, isCompact: false, isBar: false });

export const WidgetModeProvider = WidgetModeContext.Provider;

// isCompact is a separate, opt-in flag (via initNucarNext's `compact`
// option) for embeds with very little real estate — eg. the inline-embed-
// under-a-button pattern, which pushes a whole page's content down as it
// expands, unlike a modal or a full standalone page. Screens should check
// it to skip marketing copy and render just their core interactive form.
//
// isBar is a further-condensed variant (via initNucarNext's `bar` option)
// for a fixed ~100px-tall strip — eg. sitting under a VDP's main photo.
// Unlike compact mode it doesn't navigate to the next screen itself on a
// successful VIN/plate decode (there's no room to render it): VINEntryScreen
// calls the onVehicleDecoded callback (see App.js) instead, which by default
// opens a separate, full-size widget instance in the built-in modal dialog
// (lib/widgetModal.js) already on the vehicle-confirm screen.
export function useWidgetMode() {
  return useContext(WidgetModeContext);
}
